import { Country } from '@afterpay/types'
import { AddressMode, nonShippingInfoAddressModes } from '../../model'
import type { TermsContext } from './termsContext'

const isStandardOrBuyNowExpress = (isBuyNow: boolean, isExpress: boolean): boolean => {
  const isBuyNowExpress = isExpress && isBuyNow
  return !isExpress || isBuyNowExpress
}

const isDeferredShippingTerms = (isDeferredShipping: boolean, isBuyNow: boolean, addressMode: AddressMode | undefined, isExpress: boolean): boolean => {
  const deferringShippingAddressMode = isDeferredShipping && !nonShippingInfoAddressModes.includes(addressMode)
  const isExpressWithoutBuyNow = !isBuyNow && isExpress
  return deferringShippingAddressMode || isExpressWithoutBuyNow
}

const isUkRegulatedDeferredShippingTerms = (isDeferredShipping: boolean, isBuyNow: boolean, isExpress: boolean): boolean => {
  const isExpressWithoutBuyNow = !isBuyNow && isExpress
  return isDeferredShipping || isExpressWithoutBuyNow
}

const isExpressNotDeferred = (isDeferredShipping: boolean, isExpress: boolean): boolean => isExpress && !isDeferredShipping

const partialLateFeeKey = (initialCopy: string, preferredDayShown: boolean): string => `${initialCopy}${preferredDayShown ? '_preferredDayShown' : ''}`

const isUSStandardOrBuyNowExpress = (c: TermsContext): boolean => c.country === Country.US && isStandardOrBuyNowExpress(c.isBuyNow, c.isExpress)

const isDeferred = (c: TermsContext): boolean => isDeferredShippingTerms(c.isDeferredShipping, c.isBuyNow, c.addressMode, c.isExpress)

type Rule = { when: (c: TermsContext) => boolean; key: (c: TermsContext) => string }

// First-match-wins. Within each section, more-specific rules come before less-specific ones.
const RULES: Rule[] = [
  // ─── US (standard or buyNow+express) ───
  // Billing-agreement approval process — highest precedence within the US block.
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.isBillingAgreementApprovalProcess && !c.isZeroLateFee && c.shouldCreditCheck,
    key: () => 'summary:terms.estimatedPPA.lateFee_billingAgreementApprovalProcessWithCreditCheckConsent',
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.isBillingAgreementApprovalProcess && !c.isZeroLateFee,
    key: () => 'summary:terms.estimatedPPA.lateFee_billingAgreementApprovalProcess',
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.isBillingAgreementApprovalProcess && c.isZeroLateFee && c.shouldCreditCheck,
    key: () => 'summary:terms.estimatedPPA.noLateFee_billingAgreementApprovalProcessWithCreditCheckConsent',
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.isBillingAgreementApprovalProcess && c.isZeroLateFee,
    key: () => 'summary:terms.estimatedPPA.noLateFee_billingAgreementApprovalProcess',
  },
  // Existing billing-agreement (not approval-process).
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasBillingAgreements && c.isZeroLateFee && c.shouldCreditCheck,
    key: () => 'summary:terms.estimatedPPA.noLateFee_billingAgreementWithCreditCheckConsent',
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasBillingAgreements && c.isZeroLateFee,
    key: () => 'summary:terms.estimatedPPA.noLateFee_billingAgreement',
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasBillingAgreements && !c.isZeroLateFee && c.shouldCreditCheck,
    key: () => 'summary:terms.estimatedPPA.lateFee_billingAgreementWithCreditCheckConsent',
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasBillingAgreements && !c.isZeroLateFee,
    key: () => 'summary:terms.estimatedPPA.lateFee_billingAgreement',
  },
  // Estimated-PPA × late-fee matrix (no billing agreement of either kind).
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasEstimatedPPA && c.isZeroLateFee && c.shouldCreditCheck,
    key: () => 'summary:terms.estimatedPPA.noLateFee_creditCheckConsent',
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasEstimatedPPA && c.isZeroLateFee,
    key: () => 'summary:terms.estimatedPPA.noLateFee',
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && !c.hasEstimatedPPA && c.isZeroLateFee && c.shouldCreditCheck,
    key: () => 'summary:terms.noEstimatedPPA.noLateFee_creditCheckConsent',
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && !c.hasEstimatedPPA && c.isZeroLateFee,
    key: () => 'summary:terms.noEstimatedPPA.noLateFee',
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasEstimatedPPA && !c.isZeroLateFee && c.shouldCreditCheck,
    key: () => 'summary:terms.estimatedPPA.lateFee_creditCheckConsent',
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasEstimatedPPA && !c.isZeroLateFee,
    key: () => 'summary:terms.estimatedPPA.lateFee',
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && !c.hasEstimatedPPA && !c.isZeroLateFee && c.shouldCreditCheck,
    key: () => 'summary:terms.noEstimatedPPA.lateFee_creditCheckConsent',
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && !c.hasEstimatedPPA && !c.isZeroLateFee,
    key: () => 'summary:terms.noEstimatedPPA.lateFee',
  },

  // ─── US, deferred shipping with late fee (catches US users where !isStandardOrBuyNowExpress) ───
  {
    when: (c) => c.country === Country.US && isDeferred(c) && !c.isZeroLateFee && c.shouldCreditCheck,
    key: () => 'summary:terms.lateFee_deferredShippingWithCreditCheckConsent',
  },
  {
    when: (c) => c.country === Country.US && isDeferred(c) && !c.isZeroLateFee,
    key: () => 'summary:terms.lateFee_deferredShipping',
  },

  // ─── Billing agreement (any country except US-standard, which was matched above) ───
  {
    when: (c) => c.hasBillingAgreements && c.showTermsDisclosure,
    key: () => 'summary:terms_billingAgreement_termsNotAccepted',
  },
  {
    when: (c) => c.hasBillingAgreements && c.nzTermsEnabled && c.country === Country.NZ,
    key: () => 'summary:terms_billingAgreement_updated',
  },
  {
    when: (c) => c.hasBillingAgreements && c.hasPartialLateFeesEnabled,
    key: (c) => partialLateFeeKey('summary:terms_billingAgreementPartialLateFees', c.preferredDayShown),
  },
  {
    when: (c) => c.hasBillingAgreements,
    key: () => 'summary:terms_billingAgreement',
  },

  // ─── UK regulated deferred shipping (uses isUkRegulatedDeferredShippingTerms, NOT the general
  // deferred predicate; must stay above the generic deferred fallbacks so GB never matches them) ───
  {
    when: (c) => c.country === Country.GB && isUkRegulatedDeferredShippingTerms(c.isDeferredShipping, c.isBuyNow, c.isExpress),
    key: () => 'summary:termsFCA_regulated',
  },

  // ─── Deferred shipping fallback (non-US, non-UK paths) ───
  {
    when: (c) => isDeferred(c) && c.shouldCreditCheck,
    key: () => 'summary:terms_deferredShippingWithCreditCheckConsent',
  },
  {
    when: (c) => isDeferred(c) && c.showTermsDisclosure,
    key: () => 'summary:terms_deferredShipping_termsNotAccepted',
  },
  {
    when: (c) => isDeferred(c) && c.nzTermsEnabled && c.country === Country.NZ,
    key: () => 'summary:terms_deferredShipping_updated',
  },
  {
    when: (c) => isDeferred(c) && c.hasPartialLateFeesEnabled,
    key: (c) => partialLateFeeKey('summary:terms_deferredShippingPartialLateFees', c.preferredDayShown),
  },
  {
    when: isDeferred,
    key: () => 'summary:terms_deferredShipping',
  },

  // ─── NZ credit check experiment ───
  // Rules below all require isNZCreditCheckEnabled (which already implies country === NZ).
  {
    when: (c) => c.isNZCreditCheckEnabled && c.showTermsDisclosure,
    key: () => 'summary:terms_termsNotAccepted',
  },
  {
    when: (c) =>
      c.isNZCreditCheckEnabled && c.hasCreditCheckConsent === false && !c.preferredDayShown && !isExpressNotDeferred(c.isDeferredShipping, c.isExpress),
    key: () => 'summary:terms_creditCheckConsent',
  },
  {
    when: (c) => c.isNZCreditCheckEnabled && c.nzTermsEnabled && c.country === Country.NZ,
    key: (c) => (c.preferredDayShown ? 'summary:terms_preferredDayShown' : 'summary:terms_updated'),
  },

  // ─── UK regulated fallback (non-deferred) ───
  {
    when: (c) => c.country === Country.GB,
    key: () => 'summary:termsFCA_regulated',
  },

  // ─── Partial late fees (catch-all when partial-late-fees flag is on) ───
  {
    when: (c) => c.hasPartialLateFeesEnabled,
    key: (c) => partialLateFeeKey('summary:terms_partialLateFees', c.preferredDayShown),
  },

  // ─── Default ───
  { when: () => true, key: () => 'summary:terms' },
]

export const selectTermsKey = (c: TermsContext): string => {
  for (const rule of RULES) {
    if (rule.when(c)) return rule.key(c)
  }
  return 'summary:terms'
}
